<script setup>
/* global axios */
/**
 * Smarticks — appointment reminders. Plan: docs/smarticks/smarty-reminders-plan.md.
 *
 * 🔴 Save is NEVER blocked. A process is born disabled, so a half-finished draft is harmless; the
 * gate is a readiness card on ENABLE. Five save-blocking validations was the shape UX rejected —
 * two of them could not even happen, because the pickers only ever offer approved templates and
 * WhatsApp inboxes.
 */
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
// 🔴 No `import axios from 'axios'` here. That is an unauthenticated copy: it does not carry
// the rotating devise-token-auth headers, so every call below returns 401 and the screen
// renders as though nothing is configured. Use Chatwoot's global, authenticated instance --
// the same rule dashboard/api/smarticks.js states, and OnemsgWhatsapp.vue follows.

const store = useStore();
const { t } = useI18n();
const accountId = computed(() => store.getters.getCurrentAccountId);
const base = computed(
  () => `/api/v1/accounts/${accountId.value}/smarticks/reminder_configs`
);

const loading = ref(true);
// 🔴 Distinguishes "the API answered and there is nothing" from "the API did not answer".
// Without it a 401 -- a session that expired while the tab sat open -- leaves `options` at
// its empty default and the screen tells a manager with three calendars that none are
// connected, and to call support. Observed on cw 2026-09-01.
const loadError = ref(false);
const configs = ref([]);
const options = ref({ inboxes: [], connections: [], agent_inbox_id: null });
const colors = ref({});
const editing = ref(null);
const confirming = ref(null);

const fetchAll = async () => {
  loading.value = true;
  loadError.value = false;
  try {
    const [list, opts] = await Promise.all([
      axios.get(base.value),
      axios.get(`${base.value}/options`),
    ]);
    configs.value = list.data.configs;
    options.value = opts.data;
  } catch (e) {
    loadError.value = true;
    useAlert(t('SMARTICKS.REMINDERS.LOAD_FAILED'));
  } finally {
    loading.value = false;
  }
  // 🔴 Colours load AFTER the form renders and on their own endpoint: they come from Google, and
  // one auth error on a server with no key file must not stop the page appearing.
  try {
    const res = await axios.get(`${base.value}/colors`);
    colors.value = res.data.colors || {};
  } catch (e) {
    colors.value = {};
  }
};

const inboxesById = computed(() =>
  Object.fromEntries(options.value.inboxes.map(i => [i.id, i]))
);

const blank = () => ({
  name: '',
  source: 'calendar',
  source_scope: { connection_ids: [] },
  offsets_days: [1],
  send_hour: 9,
  no_response_after_hours: 24,
  inbox_id: options.value.inboxes[0]?.id ?? null,
  mode: 'closed',
  cancel_color_id: '11',
  confirm_color_id: '10',
  template: { name: '', language: 'he' },
});

// Which prerequisite is missing, so the empty state can say the ONE thing to do next rather than
// showing a form whose save refuses.
const gap = computed(() => {
  // Never diagnose a missing prerequisite from data we failed to load.
  if (loadError.value) return null;
  if (!options.value.connections.length) return 'no_calendar';
  const anyTemplate = options.value.inboxes.some(i => i.templates.length);
  if (!options.value.inboxes.length) return 'no_inbox';
  if (!anyTemplate) return 'no_template';
  return null;
});

// The readiness card. Every row is a warning, never a blocked save.
const readiness = form => {
  const inbox = inboxesById.value[form.inbox_id];
  const rows = [];
  rows.push({
    ok: !!form.template?.name,
    text: t(
      `SMARTICKS.REMINDERS.READY.${form.template?.name ? 'TEMPLATE_OK' : 'TEMPLATE_MISSING'}`
    ),
  });
  rows.push({
    ok: !!inbox,
    text: inbox
      ? t('SMARTICKS.REMINDERS.READY.INBOX_OK', { name: inbox.name })
      : t('SMARTICKS.REMINDERS.READY.INBOX_MISSING'),
  });
  const pinned = options.value.agent_inbox_id;
  rows.push({
    ok:
      form.mode !== 'agent' ||
      !pinned ||
      Number(pinned) === Number(form.inbox_id),
    text:
      form.mode === 'agent' &&
      pinned &&
      Number(pinned) !== Number(form.inbox_id)
        ? t('SMARTICKS.REMINDERS.READY.PINNED_BAD', {
            name: inboxesById.value[pinned]?.name ?? pinned,
          })
        : t('SMARTICKS.REMINDERS.READY.PINNED_OK'),
  });
  rows.push({
    ok: form.mode !== 'closed' || !inbox?.has_bot,
    text: t(
      `SMARTICKS.REMINDERS.READY.${form.mode === 'closed' && inbox?.has_bot ? 'BOT_BAD' : 'BOT_OK'}`
    ),
  });
  rows.push({
    ok: (form.source_scope?.connection_ids || []).length > 0,
    text: t('SMARTICKS.REMINDERS.READY.CALENDAR_OK'),
  });
  return rows;
};

const exampleFor = (form, offset) => {
  const appt = new Date();
  appt.setDate(appt.getDate() + 10);
  const send = new Date(appt);
  send.setDate(send.getDate() - offset);
  const d = x =>
    `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}`;
  const h = String(form.send_hour).padStart(2, '0');
  return t('SMARTICKS.REMINDERS.EXAMPLE', {
    appt: d(appt),
    send: d(send),
    hour: `${h}:00`,
  });
};

const save = async form => {
  try {
    const payload = { reminder_config: form };
    if (form.id) await axios.patch(`${base.value}/${form.id}`, payload);
    else await axios.post(base.value, payload);
    useAlert(t('SMARTICKS.REMINDERS.SAVED'));
    editing.value = null;
    await fetchAll();
  } catch (e) {
    useAlert(
      (e.response?.data?.errors || [t('SMARTICKS.REMINDERS.SAVE_FAILED')]).join(
        ' · '
      )
    );
  }
};

// Releases the batch that the enable-day stamp deliberately held back. Real paid messages, so it
// confirms first and reports what the server actually did rather than assuming it sent.
// A struck-through circle: "no colour" has to read as a deliberate option, and a bare empty
// circle looks like a colour that failed to load.
const noColourSwatch = {
  backgroundImage:
    'linear-gradient(45deg, transparent 45%, #ef4444 45%, #ef4444 55%, transparent 55%)',
};

const runToday = async config => {
  if (
    !window.confirm(
      t('SMARTICKS.REMINDERS.RUN_TODAY.CONFIRM', { name: config.name })
    )
  ) {
    return;
  }
  try {
    const res = await axios.post(`${base.value}/${config.id}/run_today`);
    useAlert(res.data.message);
    await fetchAll();
  } catch (e) {
    useAlert(
      e.response?.data?.message || t('SMARTICKS.REMINDERS.RUN_TODAY.FAILED')
    );
  }
};

const toggle = async config => {
  if (!config.enabled) {
    confirming.value = config;
    return;
  }
  await save({ ...config, enabled: false });
};

const confirmEnable = async () => {
  const config = confirming.value;
  confirming.value = null;
  await save({ ...config, enabled: true });
};

const remove = async config => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('SMARTICKS.REMINDERS.DELETE_CONFIRM', { name: config.name })
    )
  )
    return;
  await axios.delete(`${base.value}/${config.id}`);
  await fetchAll();
};

const testSend = async config => {
  // eslint-disable-next-line no-alert
  const contactId = window.prompt(t('SMARTICKS.REMINDERS.TEST.PROMPT'));
  if (!contactId) return;
  try {
    const res = await axios.post(`${base.value}/${config.id}/test`, {
      contact_id: contactId,
    });
    useAlert(res.data.message);
  } catch (e) {
    useAlert(e.response?.data?.message || t('SMARTICKS.REMINDERS.TEST.FAILED'));
  }
};

onMounted(fetchAll);
</script>

<template>
  <div class="flex flex-col gap-4 p-6" dir="rtl">
    <header class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ $t('SMARTICKS.REMINDERS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11 max-w-xl">
          {{ $t('SMARTICKS.REMINDERS.DESCRIPTION') }}
        </p>
      </div>
      <button
        v-if="!gap"
        class="px-3 py-2 text-sm rounded-lg bg-n-brand text-white"
        @click="editing = blank()"
      >
        {{ $t('SMARTICKS.REMINDERS.NEW') }}
      </button>
    </header>

    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('SMARTICKS.REMINDERS.LOADING') }}
    </div>

    <!-- A failed load is not a missing prerequisite. Say so, and offer the retry. -->
    <div
      v-else-if="loadError"
      class="p-6 border border-n-weak rounded-xl bg-n-solid-1"
    >
      <h2 class="font-medium text-n-slate-12">
        {{ $t('SMARTICKS.REMINDERS.EMPTY.LOAD_ERROR_TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('SMARTICKS.REMINDERS.EMPTY.LOAD_ERROR_BODY') }}
      </p>
      <button
        class="mt-3 px-3 py-1.5 text-sm rounded-lg bg-n-brand text-white"
        @click="fetchAll"
      >
        {{ $t('SMARTICKS.REMINDERS.EMPTY.LOAD_ERROR_RETRY') }}
      </button>
    </div>

    <!-- 🔴 The empty state BRANCHES. With no approved template every manager on every server would
         otherwise land on a form they cannot use. -->
    <div
      v-else-if="gap"
      class="p-6 border border-n-weak rounded-xl bg-n-solid-1"
    >
      <template v-if="gap === 'no_calendar'">
        <h2 class="font-medium text-n-slate-12">
          {{ $t('SMARTICKS.REMINDERS.EMPTY.NO_CALENDAR_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('SMARTICKS.REMINDERS.EMPTY.NO_CALENDAR_BODY') }}
        </p>
      </template>
      <template v-else-if="gap === 'no_inbox'">
        <h2 class="font-medium text-n-slate-12">
          {{ $t('SMARTICKS.REMINDERS.EMPTY.NO_INBOX_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('SMARTICKS.REMINDERS.EMPTY.NO_INBOX_BODY') }}
        </p>
      </template>
      <template v-else>
        <h2 class="font-medium text-n-slate-12">
          {{ $t('SMARTICKS.REMINDERS.EMPTY.NO_TEMPLATE_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('SMARTICKS.REMINDERS.EMPTY.NO_TEMPLATE_BODY') }}
        </p>
      </template>
    </div>

    <div
      v-else-if="!configs.length"
      class="p-6 border border-n-weak rounded-xl bg-n-solid-1"
    >
      <h2 class="font-medium text-n-slate-12">
        {{ $t('SMARTICKS.REMINDERS.EMPTY.NONE_TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('SMARTICKS.REMINDERS.EMPTY.NONE_BODY') }}
      </p>
    </div>

    <!-- List -->
    <template v-else>
      <div
        v-for="config in configs"
        :key="config.id"
        class="p-4 border border-n-weak rounded-xl bg-n-solid-1 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <span class="font-medium text-n-slate-12">{{ config.name }}</span>
            <span
              class="text-xs px-2 py-0.5 rounded-full"
              :class="
                config.enabled
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{
                $t(
                  `SMARTICKS.REMINDERS.STATUS.${config.enabled ? 'ON' : 'OFF'}`
                )
              }}
            </span>
            <span
              class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{
                $t(
                  `SMARTICKS.REMINDERS.MODE.${config.mode === 'closed' ? 'CLOSED' : 'AGENT'}`
                )
              }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11">
            {{
              $t('SMARTICKS.REMINDERS.STATS.SCHEDULE', {
                days: config.offsets_days.join(', '),
                hour: `${String(config.send_hour).padStart(2, '0')}:00`,
              })
            }}
            · {{ inboxesById[config.inbox_id]?.name || config.inbox_id }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{
              $t('SMARTICKS.REMINDERS.STATS.DUE', {
                count: config.stats?.due_next_run ?? 0,
              })
            }}
            ·
            {{
              $t('SMARTICKS.REMINDERS.STATS.SENT', {
                count: config.stats?.sent_today ?? 0,
              })
            }}
            ·
            <span :class="config.stats?.failed_today ? 'text-n-ruby-11' : ''">
              {{
                $t('SMARTICKS.REMINDERS.STATS.FAILED', {
                  count: config.stats?.failed_today ?? 0,
                })
              }}
            </span>
            ·
            {{
              $t('SMARTICKS.REMINDERS.STATS.CONFIRMED', {
                count: config.stats?.confirmed ?? 0,
              })
            }}
            ·
            {{
              $t('SMARTICKS.REMINDERS.STATS.DECLINED', {
                count: config.stats?.declined ?? 0,
              })
            }}
            ·
            {{
              $t('SMARTICKS.REMINDERS.STATS.CANCELLED', {
                count: config.stats?.cancelled ?? 0,
              })
            }}
            ·
            {{
              $t('SMARTICKS.REMINDERS.STATS.NO_RESPONSE', {
                count: config.stats?.no_response ?? 0,
              })
            }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="config.enabled"
            class="text-sm px-2 py-1 rounded border border-n-weak"
            @click="runToday(config)"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.RUN_TODAY') }}
          </button>
          <button
            class="text-sm px-2 py-1 rounded border border-n-weak"
            @click="testSend(config)"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.TEST') }}
          </button>
          <button
            class="text-sm px-2 py-1 rounded border border-n-weak"
            @click="editing = { ...config }"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.EDIT') }}
          </button>
          <button
            class="text-sm px-2 py-1 rounded border border-n-weak"
            @click="toggle(config)"
          >
            {{
              $t(
                `SMARTICKS.REMINDERS.ACTIONS.${config.enabled ? 'DISABLE' : 'ENABLE'}`
              )
            }}
          </button>
          <button
            class="text-sm px-2 py-1 rounded border border-n-weak text-n-ruby-11"
            @click="remove(config)"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.DELETE') }}
          </button>
        </div>
      </div>
    </template>

    <!-- Edit -->
    <div
      v-if="editing"
      class="p-4 border border-n-weak rounded-xl bg-n-solid-1 flex flex-col gap-3"
    >
      <h2 class="font-medium text-n-slate-12">
        {{
          $t(
            `SMARTICKS.REMINDERS.FORM.${editing.id ? 'EDIT_TITLE' : 'NEW_TITLE'}`
          )
        }}
      </h2>

      <label class="text-sm"
        >{{ $t('SMARTICKS.REMINDERS.FORM.NAME') }}
        <input v-model="editing.name" class="w-full mt-1" type="text" />
      </label>

      <fieldset class="text-sm">
        <legend>{{ $t('SMARTICKS.REMINDERS.FORM.CALENDARS') }}</legend>
        <label
          v-for="c in options.connections"
          :key="c.id"
          class="inline-flex items-center gap-1 me-3"
        >
          <input
            v-model="editing.source_scope.connection_ids"
            type="checkbox"
            :value="c.id"
          />
          {{ c.name }}
        </label>
      </fieldset>

      <div class="text-sm">
        <div class="flex items-center justify-between">
          <span>{{ $t('SMARTICKS.REMINDERS.FORM.WHEN') }}</span>
          <button
            class="text-xs px-2 py-1 rounded border border-n-weak"
            :disabled="editing.offsets_days.length >= 3"
            @click="editing.offsets_days.push(editing.offsets_days.at(-1) + 1)"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.ADD_STAGE') }}
          </button>
        </div>
        <div
          v-for="(o, i) in editing.offsets_days"
          :key="i"
          class="flex items-center gap-2 mt-1"
        >
          <input
            v-model.number="editing.offsets_days[i]"
            type="number"
            min="0"
            max="30"
            class="w-20"
          />
          <span class="text-xs text-n-slate-10">
            {{ $t('SMARTICKS.REMINDERS.FORM.DAYS_BEFORE') }} ·
            {{ exampleFor(editing, o) }}
          </span>
          <button
            v-if="editing.offsets_days.length > 1"
            class="text-xs text-n-ruby-11"
            @click="editing.offsets_days.splice(i, 1)"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.REMOVE') }}
          </button>
        </div>
      </div>

      <label class="text-sm"
        >{{ $t('SMARTICKS.REMINDERS.FORM.SEND_HOUR') }}
        <input
          v-model.number="editing.send_hour"
          type="number"
          min="8"
          max="20"
          class="w-24 mt-1"
        />
      </label>

      <label class="text-sm"
        >{{ $t('SMARTICKS.REMINDERS.FORM.NO_RESPONSE') }}
        <input
          v-model.number="editing.no_response_after_hours"
          type="number"
          min="1"
          max="168"
          class="w-24 mt-1"
        />
      </label>

      <label class="text-sm"
        >{{ $t('SMARTICKS.REMINDERS.FORM.INBOX') }}
        <select v-model.number="editing.inbox_id" class="w-full mt-1">
          <option v-for="i in options.inboxes" :key="i.id" :value="i.id">
            {{ i.name }} ({{ i.phone }})
          </option>
        </select>
      </label>

      <label class="text-sm"
        >{{ $t('SMARTICKS.REMINDERS.FORM.TEMPLATE') }}
        <select v-model="editing.template.name" class="w-full mt-1">
          <option value="">
            {{ $t('SMARTICKS.REMINDERS.FORM.TEMPLATE_PLACEHOLDER') }}
          </option>
          <option
            v-for="tpl in inboxesById[editing.inbox_id]?.templates || []"
            :key="tpl.name"
            :value="tpl.name"
          >
            {{ tpl.name }} ({{ tpl.language }})
          </option>
        </select>
      </label>

      <!-- 🔴 Radio CARDS with the description visible before the choice, not a select. "agent" in
           Chatwoot means a human being; a manager reading "agent mode" would think a person answers. -->
      <fieldset class="text-sm flex flex-col gap-2">
        <legend>{{ $t('SMARTICKS.REMINDERS.MODE.LEGEND') }}</legend>
        <label class="flex gap-2 p-2 border border-n-weak rounded-lg">
          <input v-model="editing.mode" type="radio" value="closed" />
          <span>
            <b>{{ $t('SMARTICKS.REMINDERS.MODE.CLOSED') }}</b>
            <br />
            <span class="text-xs text-n-slate-11">
              {{ $t('SMARTICKS.REMINDERS.MODE.CLOSED_HINT') }}
            </span>
          </span>
        </label>
        <label class="flex gap-2 p-2 border border-n-weak rounded-lg">
          <input v-model="editing.mode" type="radio" value="agent" />
          <span>
            <b>{{ $t('SMARTICKS.REMINDERS.MODE.AGENT') }}</b>
            <br />
            <span class="text-xs text-n-slate-11">
              {{ $t('SMARTICKS.REMINDERS.MODE.AGENT_HINT') }}
            </span>
          </span>
        </label>
      </fieldset>

      <!-- 🔴 Swatches, not a dropdown. Google's colour API returns hex values and no names, so the
           select could only ever read "10 — #51b749" -- which tells a clinic manager nothing about
           the colour they are picking. Showing the colour itself needs no name table and cannot
           drift from whatever palette Google serves. -->
      <div class="text-sm flex flex-wrap gap-6">
        <div>
          {{ $t('SMARTICKS.REMINDERS.FORM.CANCEL_COLOR') }}
          <div class="flex flex-wrap gap-1.5 mt-1">
            <button
              type="button"
              class="w-6 h-6 rounded-full border border-n-weak text-xs text-n-slate-11"
              :class="
                editing.cancel_color_id === ''
                  ? 'ring-2 ring-n-brand ring-offset-1'
                  : ''
              "
              :title="$t('SMARTICKS.REMINDERS.FORM.NO_COLOR')"
              :style="noColourSwatch"
              @click="editing.cancel_color_id = ''"
            />
            <button
              v-for="(c, id) in colors"
              :key="id"
              type="button"
              class="w-6 h-6 rounded-full border border-n-weak"
              :class="
                editing.cancel_color_id === id
                  ? 'ring-2 ring-n-brand ring-offset-1'
                  : ''
              "
              :style="{ backgroundColor: c.background }"
              :aria-label="c.background"
              @click="editing.cancel_color_id = id"
            />
          </div>
        </div>
        <div>
          {{ $t('SMARTICKS.REMINDERS.FORM.CONFIRM_COLOR') }}
          <div class="flex flex-wrap gap-1.5 mt-1">
            <button
              type="button"
              class="w-6 h-6 rounded-full border border-n-weak text-xs text-n-slate-11"
              :class="
                editing.confirm_color_id === ''
                  ? 'ring-2 ring-n-brand ring-offset-1'
                  : ''
              "
              :title="$t('SMARTICKS.REMINDERS.FORM.NO_COLOR')"
              :style="noColourSwatch"
              @click="editing.confirm_color_id = ''"
            />
            <button
              v-for="(c, id) in colors"
              :key="id"
              type="button"
              class="w-6 h-6 rounded-full border border-n-weak"
              :class="
                editing.confirm_color_id === id
                  ? 'ring-2 ring-n-brand ring-offset-1'
                  : ''
              "
              :style="{ backgroundColor: c.background }"
              :aria-label="c.background"
              @click="editing.confirm_color_id = id"
            />
          </div>
        </div>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ $t('SMARTICKS.REMINDERS.FORM.COLOR_HINT') }}
      </p>

      <div class="p-3 rounded-lg bg-n-solid-2 text-sm">
        <b>{{ $t('SMARTICKS.REMINDERS.READY.TITLE') }}</b>
        <div v-for="(r, i) in readiness(editing)" :key="i">
          <span :class="r.ok ? 'text-n-teal-11' : 'text-n-amber-11'">{{
            r.ok ? '✅' : '⚠'
          }}</span>
          {{ r.text }}
        </div>
      </div>

      <div class="flex gap-2">
        <button
          class="px-3 py-2 text-sm rounded-lg bg-n-brand text-white"
          @click="save(editing)"
        >
          {{ $t('SMARTICKS.REMINDERS.ACTIONS.SAVE') }}
        </button>
        <button
          class="px-3 py-2 text-sm rounded-lg border border-n-weak"
          @click="editing = null"
        >
          {{ $t('SMARTICKS.REMINDERS.ACTIONS.CANCEL') }}
        </button>
      </div>
    </div>

    <!-- 🔴 The control that spends money gets the number, not a warning paragraph. -->
    <div
      v-if="confirming"
      class="fixed inset-0 bg-black/40 flex items-center justify-center"
      @click.self="confirming = null"
    >
      <div class="bg-n-solid-1 p-6 rounded-xl max-w-md">
        <h3 class="font-medium text-n-slate-12">
          {{
            $t('SMARTICKS.REMINDERS.ENABLE.TITLE', { name: confirming.name })
          }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-2">
          {{
            $t('SMARTICKS.REMINDERS.ENABLE.BODY', {
              count: confirming.stats?.due_next_run ?? 0,
            })
          }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{
            $t('SMARTICKS.REMINDERS.ENABLE.FIRST', {
              hour: `${String(confirming.send_hour).padStart(2, '0')}:00`,
            })
          }}
        </p>
        <div class="flex gap-2 mt-4">
          <button
            class="px-3 py-2 text-sm rounded-lg bg-n-brand text-white"
            @click="confirmEnable"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.ENABLE') }}
          </button>
          <button
            class="px-3 py-2 text-sm rounded-lg border border-n-weak"
            @click="confirming = null"
          >
            {{ $t('SMARTICKS.REMINDERS.ACTIONS.CANCEL') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
